import React, { useEffect, useState } from 'react'

import { buildActionAnalyticsAttributes } from '@mondough/analytics-v2'
import { Copy, useCopy } from '@mondough/copy'
import { Text, Title } from '@mondough/monzo-ui'

import {
  getNotificationPreferences,
  setNotificationPreferences,
} from '../../../utils'
import { BottomSheet, Button, SwitchWithLabel } from '../..'
import styles from './NotificationConsentBanner.module.scss'

const NotificationConsentBanner: React.FC = () => {
  const [activityConsent, setActivityConsent] = useState(true)
  const [showBanner, setShowBanner] = useState(false)
  const c = useCopy()

  const [notificationConsentError, setNotificationConsentError] =
    React.useState(false)

  useEffect(() => {
    const fetchNotificationConsent = async () => {
      try {
        const preferences = await getNotificationPreferences()
        setActivityConsent(preferences.notifications_enabled)
        setShowBanner(preferences.using_default)
      } catch {
        // Do nothing
      }
    }
    void fetchNotificationConsent()
  }, [])

  const activityConsentSwitch = activityConsent
    ? c.string({
        key: 'app_tabs_monzo_me.notification.banner.switch.activity.sublabel.off',
        message: 'Tap to turn off',
      })
    : c.string({
        key: 'app_tabs_monzo_me.notification.banner.switch.activity.sublabel.on',
        message: 'Tap to turn on',
      })

  if (!showBanner) return null

  return (
    <BottomSheet
      id="notification-consent-banner"
      contentClassName={styles.content}
      overlayZIndex={102}
      containerZIndex={103}
    >
      <div className={styles.text}>
        <Title is="h2">
          <Copy.Simple
            id="app_tabs_monzo_me.notification.banner.title"
            fallback="Your email is used for Split notifications only"
          />
        </Title>
        <Text color="secondary">
          <Copy.Simple
            id="app_tabs_monzo_me.notification.banner.text"
            fallback="We won't send you marketing or share your email with anyone. You can always change your settings later."
          />
        </Text>
      </div>
      <SwitchWithLabel
        id="tab-notifications"
        label={c.string({
          key: 'app_tabs_monzo_me.common.settings.split_activity',
          message: 'Split activity',
        })}
        subLabel={activityConsentSwitch}
        checked={activityConsent}
        onChange={() => setActivityConsent((value) => !value)}
      />

      <Button
        theme="primary"
        fullWidth
        onClick={async () => {
          try {
            await setNotificationPreferences(activityConsent)
            setShowBanner(false)
          } catch {
            setNotificationConsentError(true)
          }
        }}
        marginTop="medium"
        className={styles.button}
        {...buildActionAnalyticsAttributes('confirm', {
          enabled: activityConsent,
        })}
      >
        <Copy.Simple
          id="app_tabs_monzo_me.notification.banner.button"
          fallback="Got it"
        />
      </Button>
      {notificationConsentError && (
        <Text size="small" color="destructive">
          <Copy.Simple
            id="app_tabs_monzo_me.common.error.generic_message"
            fallback="Something went wrong. Please try again."
          />
        </Text>
      )}
    </BottomSheet>
  )
}

export default NotificationConsentBanner
